import React from 'react';
import { translate } from '../../../common/utility/DisplayPrep';

// Component
import { Tooltip } from '../../../components/Tooltip';
import { LineClamp } from '../../../components/LineClamp';

const { READ_ONLY_FEATURES } = process.env;

const actions = READ_ONLY_FEATURES === 'true' ? ['edit'] : ['edit', 'delete', 'copy', 'export'];

const defaultUiConfig = {
    name: 'Criteria',
    withActions: [...actions],
    primaryKey: 'criteriaId',
    pageSize: 10,
    onFormMount: (data, hookFxns) => {
        if (data?.criteriaTypeDTO) {
            hookFxns.updateCriteriaType(data.criteriaTypeDTO?.criteriaTypeId);
        }

        return null;
    },
    properties: {
        criteriaId: {
            name: 'ID',
            sortable: true,
            input: (v) => {
                return typeof v.criteriaId !== 'undefined' ? 'valueDisplay' : null;
            }
        },
        name: {
            name: 'Name',
            sortable: true,
            rowDisplay: true,
            nullable: false,
            max: 512,
            min: 0,
            input: 'text',
            trimOnSave: true,
            additionalProperties: {
                sortType: (rowA, rowB, id, desc) => {
                    return (rowA?.values?.name ?? '').localeCompare(rowB?.values?.name ?? '', 'en');
                }
            }
        },
        description: {
            name: 'Description',
            defaultContent: '',
            min: 0,
            max: 2048,
            rowDisplay: true,
            sortable: true,
            nullable: false,
            input: 'multiline',
            trimOnSave: true,
            additionalProperties: {
                Cell: ({ cell: { value } }) => {
                    return (
                        <Tooltip
                            transitionFlip={false}
                            followCursor={false}
                            title={value}
                            trigger="mouseenter"
                            delay={1000}
                            arrow={true}
                        >
                            <LineClamp content={value} />
                        </Tooltip>
                    );
                }
            },
            inputProps: {
                height: '11rem'
            }
        },
        labelTemplate: {
            name: 'Label Template',
            defaultContent: '',
            min: 0,
            max: 2048,
            rowDisplay: false,
            sortable: false,
            nullable: true,
            input: 'multiline',
            inputProps: {
                height: '6rem'
            }
        },
        criteriaTypeDTO: {
            name: 'Type',
            sortable: true,
            rowDisplay: true,
            nullable: false,
            input: 'select',
            accessorOverride: (v) => {
                return v && v?.criteriaTypeId;
            },
            postChangeBind: (v, onChange, hookFxns) => {
                hookFxns.updateCriteriaType(v);
            },
            additionalProperties: {
                Cell: ({ cell }) => {
                    return cell?.row?.original?.criteriaTypeDTO?.name ?? '';
                },
                sortType: (rowA, rowB) => {
                    return (rowA?.original?.criteriaTypeDTO?.name ?? '').localeCompare(
                        rowB?.original?.criteriaTypeDTO?.name ?? '',
                        'en'
                    );
                }
            }
        },
        method: {
            name: 'Method',
            rowDisplay: false,
            nullable: false,
            input: 'select'
        },
        uuid: {
            rowDisplay: false,
            sortable: true,
            name: 'Uuid'
        },
        lastModId: {
            rowDisplay: false,
            sortable: true,
            name: 'Last Modified ID'
        },
        lastModDatetime: {
            rowDisplay: true,
            displayPrep: 'FullDate',
            name: 'Updated',
            additionalProperties: {
                Cell: ({ cell: { value } }) => {
                    return translate.ReadableDateTimeColumn(value);
                }
            }
        },
        createId: {
            rowDisplay: false,
            name: 'Create ID'
        },
        createDatetime: {
            rowDisplay: false,
            name: 'Create Datetime'
        },
        timeboxEligible: {
            defaultContent: false,
            rowDisplay: false,
            name: 'Timebox Eligible?',
            input: 'checkbox',
            inputProps: {
                checkboxlabel: 'Timebox Eligible?'
            }
        },
        implemented: {
            defaultContent: true,
            rowDisplay: false,
            name: 'Is Implemented?',
            input: 'checkbox',
            inputProps: {
                checkboxlabel: 'Is Implemented?'
            }
        }
    },
    newDoc: {
        createDatetime: null,
        createId: null,
        criteriaResourceParams: [],
        dtoState: 'NEW',
        lastModDatetime: null,
        lastModId: null,
        name: '',
        resourceId: null,
        resourceType: null,
        auditId: null,
        description: '',
        implemented: true
    },
    sortBy: {
        columnId: 'name',
        desc: false
    }
};

export default defaultUiConfig;
